// Value imports from the rewards page are loaded on demand: this SDK route is
// built into the shared API client (entry graph), so a static import would pin
// the rewards adapters into the first-paint chunk on every page. The page chunk
// imports the same modules statically, so on /rewards these resolve instantly.
import type {
  ActivityCommissions,
  ActivityInvitations,
  ActivitySettlements,
  ActivityTrades,
} from '@/client/pages/activity-rewards-live/details-adapter';
import type {
  ActivityClaimProof,
  ActivityEnrollmentConfig,
  ActivityEnrollmentStatus,
  ActivityOverview,
  EnrollRequest,
} from '@/client/pages/activity-rewards-live/types';
import ky, { type Options } from 'ky';

// Optional filters of the rewards v2 read APIs; empty values are not sent.
type Query = Record<string, string | number | undefined>;
export type InvitationsQuery = { scope?: 'period' | 'total'; page?: number; page_size?: number };
export type CommissionsQuery = {
  period?: number;
  invitee_ref?: string;
  event_type?: string;
  page?: number;
  page_size?: number;
};

export type TradesQuery = {
  period?: number;
  event_type?: string;
  side?: string;
  state?: string;
  page?: number;
  page_size?: number;
};
export type PageQuery = { page?: number; page_size?: number };

const searchParams = (query: Query) =>
  Object.entries(query).filter((entry): entry is [string, string | number] => entry[1] !== undefined && entry[1] !== '');

export type ActivityRewardsApi = {
  getConfig: (options?: Options) => Promise<ActivityEnrollmentConfig>;
  getEnrollment: (options?: Options) => Promise<ActivityEnrollmentStatus>;
  enroll: (request: EnrollRequest, expectedActivityId: string, options?: Options) => Promise<ActivityEnrollmentStatus>;
  getClaimProof: (options?: Options) => Promise<ActivityClaimProof>;
  getOverview: (options?: Options) => Promise<ActivityOverview>;
  getInvitations: (query: InvitationsQuery, options?: Options) => Promise<ActivityInvitations>;
  getCommissions: (query: CommissionsQuery, options?: Options) => Promise<ActivityCommissions>;
  getTrades: (query: TradesQuery, options?: Options) => Promise<ActivityTrades>;
  getSettlements: (query: PageQuery, options?: Options) => Promise<ActivitySettlements>;
};

const loadAdapter = () => import('@/client/pages/activity-rewards-live/adapter');
const loadDetailsAdapter = () => import('@/client/pages/activity-rewards-live/details-adapter');
const loadRateLimit = () => import('@/client/pages/activity-rewards-live/rate-limit');

export const createActivityRewardsApi = (options: Options): ActivityRewardsApi => {
  const api = ky.create({
    ...options,
    retry: 0,
    prefixUrl: `${String(options.prefixUrl).replace(/\/$/, '')}/activity-rewards`,
    hooks: {
      ...options.hooks,
      afterResponse: [
        async (_request, _options, response) => {
          // Let the shared auth hook clear an expired session. Business errors
          // retain their reason before the legacy hook replaces it with text.
          if (!response.ok && response.status !== 401) {
            const body = (await response
              .clone()
              .json()
              .catch(() => null)) as { reason?: unknown; msg?: unknown } | null;
            const [{ ActivityEnrollmentError }, { parseRetryAfter }] = await Promise.all([loadAdapter(), loadRateLimit()]);
            throw new ActivityEnrollmentError(
              typeof body?.msg === 'string' && body.msg ? body.msg : '报名暂时不可用，请稍后重试。',
              typeof body?.reason === 'string' ? body.reason : 'REQUEST_FAILED',
              response.status,
              // A 429 is transient; callers decide how long to wait from Retry-After.
              response.status === 429 ? parseRetryAfter(response.headers.get('Retry-After')) : undefined
            );
          }
        },
        ...(options.hooks?.afterResponse || []),
      ],
    },
  });
  return {
    getConfig: async requestOptions =>
      (await loadAdapter()).parseActivityConfig(await api.get('config', requestOptions).json()),
    getEnrollment: async requestOptions =>
      (await loadAdapter()).parseActivityStatus(await api.get('enrollment', requestOptions).json()),
    getClaimProof: async requestOptions =>
      (await loadAdapter()).parseClaimProof(await api.get('claim-proof', requestOptions).json()),
    getOverview: async requestOptions =>
      (await loadAdapter()).parseOverview(await api.get('overview', requestOptions).json()),
    getInvitations: async (query, requestOptions) =>
      (await loadDetailsAdapter()).parseInvitations(
        await api.get('invitations', { ...requestOptions, searchParams: searchParams(query) }).json()
      ),
    getCommissions: async (query, requestOptions) =>
      (await loadDetailsAdapter()).parseCommissions(
        await api.get('commissions', { ...requestOptions, searchParams: searchParams(query) }).json()
      ),
    getTrades: async (query, requestOptions) =>
      (await loadDetailsAdapter()).parseTrades(
        await api.get('trades', { ...requestOptions, searchParams: searchParams(query) }).json()
      ),
    getSettlements: async (query, requestOptions) =>
      (await loadDetailsAdapter()).parseSettlements(
        await api.get('settlements', { ...requestOptions, searchParams: searchParams(query) }).json()
      ),
    enroll: async (request, expectedActivityId, requestOptions) => {
      const headers = new Headers(requestOptions?.headers as HeadersInit | undefined);
      headers.set('X-Activity-Id', expectedActivityId);
      return (await loadAdapter()).parseActivityStatus(
        await api
          .post('enrollment', {
            ...requestOptions,
            headers,
            json: request,
            retry: 0,
          })
          .json()
      );
    },
  };
};
